import yaml from 'js-yaml';
import type React from 'react';
import { useEffect, useState } from 'react';
import SwaggerUI from 'swagger-ui-react';

import 'swagger-ui-react/swagger-ui.css';
import { Card } from '@/components/ui/card';

interface OpenAPISpec {
  openapi?: string;
  swagger?: string;
  [key: string]: unknown;
}

interface OpenAPIViewerProps {
  spec: string | object;
  className?: string;
}

export const OpenAPIViewer: React.FC<OpenAPIViewerProps> = ({ spec, className = '' }) => {
  const [parsedSpec, setParsedSpec] = useState<OpenAPISpec | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    try {
      let parsed: OpenAPISpec;
      
      if (typeof spec === 'string') {
        // Try to parse as JSON first
        try {
          parsed = JSON.parse(spec) as OpenAPISpec;
        } catch {
          // If JSON parsing fails, try YAML
          try {
            parsed = yaml.load(spec) as OpenAPISpec;
          } catch {
            throw new Error('Failed to parse as JSON or YAML');
          }
        }
      } else {
        parsed = spec as OpenAPISpec;
      }

      // Validate that it's an OpenAPI/Swagger spec
      if (!parsed.openapi && !parsed.swagger) {
        throw new Error('Not a valid OpenAPI/Swagger specification');
      }

      setParsedSpec(parsed);
      setError('');
    } catch (err) {
      console.error('Error parsing OpenAPI spec:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse specification');
    }
  }, [spec]);

  useEffect(() => {
    // Custom styling for Swagger UI
    const style = document.createElement('style');
    style.textContent = `
      .swagger-ui .topbar {
        display: none;
      }
      .swagger-ui .info {
        margin: 0;
      }
      .swagger-ui .scheme-container {
        background: transparent;
        box-shadow: none;
        padding: 0;
      }
      .dark .swagger-ui {
        filter: invert(0.88) hue-rotate(180deg);
      }
      .dark .swagger-ui img {
        filter: invert(1) hue-rotate(180deg);
      }
      .swagger-ui .btn {
        box-shadow: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (error) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="text-red-600 dark:text-red-400">
          <h3 className="font-semibold mb-2">Error Loading OpenAPI Specification</h3>
          <p className="text-sm">{error}</p>
        </div>
      </Card>
    );
  }

  if (!parsedSpec) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-6 ${className} overflow-hidden`}>
      <div className="swagger-wrapper">
        <SwaggerUI
          spec={parsedSpec}
          docExpansion="list"
          defaultModelsExpandDepth={1}
          defaultModelExpandDepth={1}
          displayRequestDuration={true}
          filter={true}
          showExtensions={true}
          showCommonExtensions={true}
          tryItOutEnabled={true}
          displayOperationId={false}
          persistAuthorization={true}
        />
      </div>
    </Card>
  );
};